import React, { useCallback, useState } from 'react';
import Layout from '../layout/Layout';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { uploadDataset } from './UploadAPI';

const FileUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');

  const handleFiles = useCallback((fileList) => {
    setFiles(Array.from(fileList || []));
    setMessage('');
    setProgress(0);
  }, []);

  const handleInputChange = useCallback(
    (event) => {
      handleFiles(event.target.files);
    },
    [handleFiles],
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        handleFiles(event.dataTransfer.files);
        event.dataTransfer.clearData();
      }
    },
    [handleFiles],
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const upload = useCallback(async () => {
    if (!files.length || uploading) {
      return;
    }
    setUploading(true);
    setProgress(0);
    setMessage('');

    let successCount = 0;

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file, file.name);

        await uploadDataset(formData);
        successCount += 1;
        setProgress(Math.round(((i + 1) / files.length) * 100));
      }

      setMessage(`Uploaded ${successCount}/${files.length} files successfully. We will train your custom singing voice model soon. You will receive an email when it's ready.`);
    } catch (e) {
      setMessage(e.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }, [files, uploading]);

  return (
    <Layout>
      <div style={{ maxWidth: 640, margin: '40px auto', padding: '0 16px' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Custom Singing Voice Training Demo
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          Upload your clean vocal recordings to
          train a custom singing voice model just for you.
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Recommended: high‑quality, isolated vocal stems (WAV/FLAC/AIFF) with at
          least 30 minutes of material. Avoid noisy mixes, backing tracks, or
          copyrighted content you do not have rights to use.
        </Typography>

        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          style={{
            border: '2px dashed #999',
            borderRadius: 8,
            padding: 32,
            textAlign: 'center',
            marginBottom: 16,
          }}
        >
          <Typography variant="body1" gutterBottom>
            Drag &amp; drop files here
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            or
          </Typography>
          <input
            id="file-input"
            type="file"
            multiple
            onChange={handleInputChange}
            style={{ display: 'none' }}
          />
          <label htmlFor="file-input">
            <Button variant="outlined" component="span">
              Browse files
            </Button>
          </label>
        </div>

        {files.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Typography variant="subtitle1" gutterBottom>
              Selected files
            </Typography>
            <ul style={{ paddingLeft: 16 }}>
              {files.map((file) => (
                <li key={file.name}>
                  <Typography variant="body2">
                    {file.name} ({Math.round(file.size / 1024)} KB)
                  </Typography>
                </li>
              ))}
            </ul>
          </div>
        )}

        {uploading && (
          <div style={{ marginBottom: 16 }}>
            <LinearProgress variant="indeterminate" />
          </div>
        )}

        {message && (
          <Typography
            variant="body2"
            color={message.includes('success') ? 'primary' : 'error'}
            gutterBottom
          >
            {message}
          </Typography>
        )}

        <Button
          variant="contained"
          color="primary"
          disabled={!files.length || uploading}
          onClick={upload}
        >
          {uploading ? 'Uploading…' : 'Start training upload'}
        </Button>

        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 24 }}
          paragraph
        >
          Privacy: Your uploaded files and training datasets are used only to
          train your custom singing voice model. We do not share, sell, or
          redistribute your audio or dataset. All uploaded data will be deleted
          after the training process is completed.
        </Typography>

        {/* Endpoint is handled by the shared API layer */}
      </div>
    </Layout>
  );
};

export default FileUpload;

